import {
  CATEGORIES_D_ASSOCIATION,
  type AssociationDuServeur,
  type CategorieDAssociation,
  type FicheDAssociation,
  type GeoServer,
  type PageDAssociation,
  type RoleDansLAsso,
} from '@sirocco/shared';
import { requireUid } from './auth';
import { paths } from './paths';
import { loadUsers, unknownUser } from './records';
import { noterArriveeDansLeGroupeDeSortie } from './messages';
import { readOptionalKeys, readOptionalValue, SiroccoDataError, updateValues, writeValue } from './rtdb';
import { langue, t } from '../i18n';

/**
 * Les associations d'une ecole.
 *
 * Le catalogue vit dans la fiche du serveur (`servers/{id}/associations`),
 * pose par l'administration. Qui en est vit a part, par association :
 * `associationMembres/{serveur}/{asso}/{uid}` vaut 'chef' ou 'membre', et
 * tout membre de l'ecole le lit. On y entre par une demande
 * (`associationDemandes`) que seul le chef lit et accepte ; les regles
 * refusent qu'on s'y ajoute soi-meme ou qu'on se nomme chef.
 *
 * Tout le monde lit le fil et les evenements d'une association ; seuls
 * ses membres y ecrivent. Voir `publishPost` et la regle des evenements.
 */

type Catalogue = NonNullable<GeoServer['associations']>;
type Roles = Record<string, RoleDansLAsso>;

async function catalogueDe(serverId: string): Promise<Catalogue> {
  return (await readOptionalValue<Catalogue>(`${paths.server(serverId)}/associations`)) ?? {};
}

/** Dans l'ordre de l'ecole ; la cle departage deux rangs egaux. */
function rangees(catalogue: Catalogue): [string, Catalogue[string]][] {
  return Object.entries(catalogue).sort(([cleA, a], [cleB, b]) => a.ordre - b.ordre || cleA.localeCompare(cleB));
}

/**
 * Ce que la carte montre d'une association : sa description, son logo,
 * sa couleur, sa famille.
 *
 * La fiche a part fait foi ; le catalogue d'avant le 9 octobre 2026, qui
 * portait tout, sert de repli le temps que l'outil ait repasse. Seul
 * l'outil ecrit l'un et l'autre, mais une adresse et une couleur vont dans
 * un `src` et dans un style : on ne laisse passer que ce qui a la forme
 * attendue.
 */
function habillage(
  entree: Catalogue[string],
  fiche: FicheDAssociation | null | undefined,
): Pick<AssociationDuServeur, 'description' | 'logo' | 'banniere' | 'fond' | 'encre' | 'categorie'> {
  const source: FicheDAssociation = fiche ?? entree;
  const https = (adresse: string | undefined) => (adresse?.startsWith('https://') ? adresse : null);
  /* Avant les bannieres, `banniere` portait le logo : sans `logo`, c'en est un. */
  const logo = https(source.logo) ?? (source.logo === undefined ? https(source.banniere) : null);
  const banniere = source.logo !== undefined ? https(source.banniere) : null;
  const fond = logo && /^#[0-9a-f]{6}$/i.test(source.fond ?? '') ? source.fond! : null;
  const categorie = (CATEGORIES_D_ASSOCIATION as readonly string[]).includes(entree.categorie ?? '')
    ? (entree.categorie as CategorieDAssociation)
    : null;
  const encre = banniere && (source.encre === 'claire' || source.encre === 'sombre') ? source.encre : null;
  return { description: source.description ?? '', logo, banniere, fond, encre, categorie };
}

/** Toutes les associations, avec ma place dans chacune. */
export async function listerLesAssociations(serverId: string): Promise<AssociationDuServeur[]> {
  const uid = requireUid();
  const [catalogue, roles, fiches] = await Promise.all([
    catalogueDe(serverId),
    readOptionalValue<Record<string, Roles>>(paths.associationMembres(serverId)),
    readOptionalValue<Record<string, FicheDAssociation>>(paths.associationsFiches(serverId)),
  ]);
  const liste = rangees(catalogue);
  /* Mes demandes : je ne lis que la mienne, association par association. */
  const demandes = await Promise.all(
    liste.map(([id]) => readOptionalValue<{ at: number }>(paths.associationDemande(serverId, id, uid))),
  );
  return liste.map(([id, entree], rang) => ({
    id,
    nom: entree.nom,
    membres: Object.keys(roles?.[id] ?? {}).length,
    monRole: roles?.[id]?.[uid] ?? null,
    maDemande: demandes[rang] !== null,
    ...habillage(entree, fiches?.[id]),
  }));
}

/** La page d'une association : ses membres, ma place, et pour son chef les demandes. */
export async function lireLAssociation(serverId: string, asso: string): Promise<PageDAssociation> {
  const uid = requireUid();
  const [catalogue, roles, maDemande, bloques, masques, ficheAPart] = await Promise.all([
    catalogueDe(serverId),
    readOptionalValue<Roles>(paths.associationMembresDe(serverId, asso)),
    readOptionalValue<{ at: number }>(paths.associationDemande(serverId, asso, uid)),
    readOptionalKeys(paths.blockedUsers(uid)),
    readOptionalKeys(paths.hiddenUsers(uid)),
    readOptionalValue<FicheDAssociation>(paths.associationFiche(serverId, asso)),
  ]);
  const fiche = catalogue[asso];
  if (!fiche) throw new SiroccoDataError(t.messages.annuaire.associations.introuvable, 'not-found');

  const monRole = roles?.[uid] ?? null;
  /* Les demandes ne se lisent que par le chef : pour les autres, rien a demander a la base. */
  const brutes =
    monRole === 'chef'
      ? ((await readOptionalValue<Record<string, { at: number }>>(paths.associationDemandes(serverId, asso))) ?? {})
      : {};
  const idsDemandes = Object.entries(brutes)
    .sort(([, a], [, b]) => (a.at ?? 0) - (b.at ?? 0))
    .map(([id]) => id);
  const idsMembres = Object.keys(roles ?? {});
  const users = await loadUsers([...idsMembres, ...idsDemandes]);
  /* Bloque ou masque : je ne le vois pas plus ici qu'ailleurs. */
  const ecartes = new Set([...bloques, ...masques]);

  const membres = idsMembres
    .filter((id) => id === uid || !ecartes.has(id))
    .map((id) => ({ user: users.get(id) ?? unknownUser(id), role: roles![id] }))
    .sort(
      (a, b) =>
        Number(b.role === 'chef') - Number(a.role === 'chef') ||
        a.user.username.localeCompare(b.user.username, langue, { sensitivity: 'base' }),
    );

  return {
    association: {
      id: asso,
      nom: fiche.nom,
      membres: idsMembres.length,
      monRole,
      maDemande: maDemande !== null,
      ...habillage(fiche, ficheAPart),
    },
    membres,
    demandes: idsDemandes.filter((id) => !ecartes.has(id)).map((id) => users.get(id) ?? unknownUser(id)),
  };
}

export async function demanderARejoindre(serverId: string, asso: string): Promise<void> {
  await writeValue(paths.associationDemande(serverId, asso, requireUid()), { at: Date.now() });
}

export async function annulerMaDemande(serverId: string, asso: string): Promise<void> {
  await writeValue(paths.associationDemande(serverId, asso, requireUid()), null);
}

/** Le chef accepte : la demande devient une adhesion, dans la meme ecriture. */
export async function accepter(serverId: string, asso: string, autre: string): Promise<void> {
  await updateValues({
    [paths.associationMembre(serverId, asso, autre)]: 'membre',
    [paths.associationDemande(serverId, asso, autre)]: null,
  });
}

export async function refuser(serverId: string, asso: string, autre: string): Promise<void> {
  await writeValue(paths.associationDemande(serverId, asso, autre), null);
}

/** Le chef retire un membre ; un autre chef, lui, ne se retire que par l'administration. */
export async function retirer(serverId: string, asso: string, autre: string): Promise<void> {
  await writeValue(paths.associationMembre(serverId, asso, autre), null);
}

export async function quitter(serverId: string, asso: string): Promise<void> {
  await writeValue(paths.associationMembre(serverId, asso, requireUid()), null);
}

/**
 * Le groupe de l'association, pour qui en est membre : son identifiant de
 * conversation, ou y revenir si on l'avait quitte.
 *
 * La fonction `tenirLeGroupeDeLAsso` le cree et y fait entrer chaque
 * nouveau membre. Celui qui l'a quitte sans quitter l'association y
 * revient d'ici : la regle des membres lui ouvre cette porte, et a lui
 * seul.
 */
export async function ouvrirLeGroupe(serverId: string, asso: string): Promise<string> {
  const uid = requireUid();
  const id = await readOptionalValue<string>(paths.associationGroupe(serverId, asso));
  if (!id) throw new SiroccoDataError(t.messages.annuaire.associations.groupePasPret, 'groupe-absent');
  const dedans = await readOptionalValue<boolean>(paths.userConversation(uid, id));
  if (!dedans) {
    await updateValues({
      [paths.conversationMember(id, uid)]: true,
      [paths.userConversation(uid, id)]: true,
    });
    await noterArriveeDansLeGroupeDeSortie(id, uid);
  }
  return id;
}
